import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useTwoFactorSendMutation, useTwoFactorVerifyMutation } from "@/store/rest_api";
import { useAuth } from "@/hooks/useAuth";
import { authService } from "@/services/authService";
import { apiErrorMessage } from "@/util/apiError";
import { DEVICE_PLATFORM, getDeviceName, getDeviceToken, setDeviceToken } from "@/util/device";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import { TwoFactorNavigationState } from "../model/interface/TwoFactor";

const RESEND_COOLDOWN_SECONDS = 30;

export default function TwoFactorVerifyForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshAuthStatus } = useAuth();
  const challenge = location.state as TwoFactorNavigationState | null;

  const [code, setCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [method, setMethod] = useState(challenge?.method);
  const [maskedPhone, setMaskedPhone] = useState(challenge?.maskedPhone);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);

  const [verify, verifyResult] = useTwoFactorVerifyMutation();
  const [send, sendResult] = useTwoFactorSendMutation();

  const submitCode = (value: string) => {
    if (!challenge || verifyResult.isLoading) {
      return;
    }
    verify({
      challengeToken: challenge.challengeToken,
      code: value,
      rememberDevice,
      deviceToken: getDeviceToken() ?? undefined,
      deviceName: getDeviceName(),
      devicePlatform: DEVICE_PLATFORM,
    });
  };

  const resend = () => {
    if (!challenge) {
      return;
    }
    send({ challengeToken: challenge.challengeToken });
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
  };

  const switchToSms = () => {
    if (!challenge) {
      return;
    }
    send({ challengeToken: challenge.challengeToken, method: "SMS" });
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
  };

  useEffect(() => {
    if (!challenge) {
      navigate("/login", { replace: true });
    }
  }, [challenge, navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return;
    }
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (sendResult.isSuccess) {
      setMethod(sendResult.data.method);
      setMaskedPhone(sendResult.data.maskedPhone);
      setCode("");
    }
  }, [sendResult]);

  useEffect(() => {
    if (verifyResult.isSuccess) {
      const { accessToken, refreshToken, deviceToken } = verifyResult.data;
      authService.setAccessToken(accessToken);
      authService.setRefreshToken(refreshToken);
      if (deviceToken) {
        setDeviceToken(deviceToken);
      }
      refreshAuthStatus().then(() => navigate(challenge?.redirect ?? "/dashboard"));
    }
  }, [verifyResult, navigate, refreshAuthStatus, challenge]);

  if (!challenge) {
    return null;
  }

  const usingSms = method === "SMS";

  return (
    <div className="w-full max-w-md space-y-6 text-foreground">
      <div className="flex justify-center">
        <Link to="/">
          <img src="/assets/images/retreevo-logo.svg" alt="" className="h-10 w-auto" />
        </Link>
      </div>

      <div className="rounded-xl border bg-card shadow-sm p-8 space-y-6">
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Two-factor authentication</h1>
          <p className="text-sm text-muted-foreground">
            {usingSms
              ? `Enter the 6-digit code we sent to ${maskedPhone ?? "your phone"}.`
              : "Enter the 6-digit code from your authenticator app."}
          </p>
          {usingSms && (
            <p className="text-xs text-muted-foreground">
              SMS codes are billed at $0.01 each. Authenticator app codes are free; you can switch
              in Settings once signed in.
            </p>
          )}
        </div>

        <div className="flex justify-center">
          <InputOTP
            maxLength={6}
            value={code}
            onChange={setCode}
            onComplete={submitCode}
            autoFocus
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="rememberDevice"
            checked={rememberDevice}
            onCheckedChange={(checked) => setRememberDevice(checked === true)}
          />
          <Label htmlFor="rememberDevice" className="text-sm font-normal">
            Trust this device for 30 days
          </Label>
        </div>

        {verifyResult.isError && (
          <p className="text-sm text-destructive">
            {apiErrorMessage(verifyResult.error, "Invalid code. Please try again.")}
          </p>
        )}
        {sendResult.isError && (
          <p className="text-sm text-destructive">
            {apiErrorMessage(sendResult.error, "Unable to send the code. Please try again.")}
          </p>
        )}

        <Button
          className="w-full"
          disabled={code.length < 6 || verifyResult.isLoading}
          onClick={() => submitCode(code)}
        >
          {verifyResult.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Verify
        </Button>

        <div className="space-y-2 text-center text-sm text-muted-foreground">
          {usingSms && (
            <p>
              Didn't get the code?{" "}
              <button
                type="button"
                className="font-medium text-foreground hover:text-primary disabled:opacity-50"
                disabled={resendCooldown > 0 || sendResult.isLoading}
                onClick={resend}
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
              </button>
            </p>
          )}
          {!usingSms && challenge.smsFallbackAvailable && (
            <p>
              Can't use your authenticator app?{" "}
              <button
                type="button"
                className="font-medium text-foreground hover:text-primary disabled:opacity-50"
                disabled={sendResult.isLoading}
                onClick={switchToSms}
              >
                Send a code by SMS ($0.01)
              </button>
            </p>
          )}
          <p>
            <Link to="/login" className="hover:text-foreground hover:underline">
              Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
